import { useCallback } from 'react';
import type { To } from 'react-router-dom';
import { useLanguage, type Lang } from '@/contexts/LanguageContext';
import { SEO_DEFAULT_LOCALE, SEO_LOCALE_URL_STRATEGY } from '@/lib/seo';
import {
  buildLocaleAwarePath,
  detectLocaleFromPathname,
  normalizeLocaleUrlStrategy,
  stripLocalePrefixFromPath,
} from './seo.shared.js';

export { detectLocaleFromPathname, stripLocalePrefixFromPath };

export function getLogicalPath(pathname: string): string {
  return stripLocalePrefixFromPath(pathname);
}

export function localizePath(pathname: string, locale: Lang): string {
  const strategy = normalizeLocaleUrlStrategy(SEO_LOCALE_URL_STRATEGY);
  if (strategy !== 'path') return pathname;

  return buildLocaleAwarePath({
    pathname: stripLocalePrefixFromPath(pathname),
    locale,
    localeUrlStrategy: 'path',
    defaultLocale: SEO_DEFAULT_LOCALE,
  });
}

export function swapLocalePath(fullPath: string, targetLocale: Lang): string {
  const hashIndex = fullPath.indexOf('#');
  const pathAndQuery = hashIndex >= 0 ? fullPath.slice(0, hashIndex) : fullPath;
  const hashSuffix = hashIndex >= 0 ? fullPath.slice(hashIndex) : '';

  const queryIndex = pathAndQuery.indexOf('?');
  const pathname = queryIndex >= 0 ? pathAndQuery.slice(0, queryIndex) : pathAndQuery;
  const querySuffix = queryIndex >= 0 ? pathAndQuery.slice(queryIndex) : '';

  return `${localizePath(pathname, targetLocale)}${querySuffix}${hashSuffix}`;
}

export function localizeTo(to: To, locale: Lang): To {
  const strategy = normalizeLocaleUrlStrategy(SEO_LOCALE_URL_STRATEGY);
  if (strategy !== 'path') return to;

  if (typeof to === 'string') {
    return swapLocalePath(to, locale);
  }

  if (typeof to === 'object' && to !== null && 'pathname' in to && typeof to.pathname === 'string') {
    return {
      ...to,
      pathname: localizePath(to.pathname, locale),
    };
  }

  return to;
}

export function useLocalizedPath() {
  const { lang } = useLanguage();
  return useCallback((pathname: string) => localizePath(pathname, lang), [lang]);
}
